import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY || "0x4AAAAAAEQqUjQzAr2WQEIH";
const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileLoader: Promise<TurnstileApi> | null = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoader) return turnstileLoader;

  turnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
    const finish = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile unavailable"));
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", finish, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
    document.head.append(script);
  });

  return turnstileLoader;
}

export type TurnstileHandle = {
  reset: () => void;
};

type TurnstileWidgetProps = {
  action: "event_register" | "family_join" | "contact";
  onToken: (token: string) => void;
  onError: () => void;
};

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { action, onToken, onError },
  forwardedRef,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef("");
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const [failed, setFailed] = useState(false);

  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useImperativeHandle(forwardedRef, () => ({
    reset() {
      onTokenRef.current("");
      if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
    },
  }), []);

  useEffect(() => {
    let active = true;
    setFailed(false);
    void loadTurnstile().then((turnstile) => {
      if (!active || !containerRef.current) return;
      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        action,
        theme: "light",
        callback: (token: string) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(""),
        "error-callback": () => {
          onTokenRef.current("");
          onErrorRef.current();
        },
      });
    }).catch(() => {
      if (!active) return;
      setFailed(true);
      onErrorRef.current();
    });

    return () => {
      active = false;
      if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = "";
      onTokenRef.current("");
    };
  }, [action]);

  return <div className={`turnstile-widget${failed ? " turnstile-widget--error" : ""}`} ref={containerRef} />;
});
